import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SettingsGroupProps {
  /** The sentence-case kicker above the card — "Your account", "Privacy". */
  label?: string
  /** Sits opposite the label: "Push, email", a "Log out of other devices" button. */
  action?: ReactNode
  /**
   * The card's side padding: `hub` 20 (the Account hub, mock `ac-card`), `page` 24 (every
   * sub-page, mock `ac-pane .ac-card`). A row's hover wash bleeds exactly this far, to the card's
   * edge, through `--row-bleed`.
   */
  inset?: 'hub' | 'page'
  /** Replaces the card's own padding: a grid of tiles, a theme picker. Rows still bleed by `inset`. */
  bodyClassName?: string | undefined
  children: ReactNode
  className?: string
}

const INSET = {
  hub: 'px-5 [--row-bleed:--spacing(5)]',
  page: 'px-6 [--row-bleed:--spacing(6)]',
} as const

/**
 * One block of an Account page: a kicker, then a card of rows.
 *
 * Not `SectionCard`. That one puts its eyebrow INSIDE the card and pads to 24px
 * all round, which is right for a Home panel carrying a chart; the settings
 * blocks put the label outside and above, and the card itself is only a frame
 * around a list of rows.
 *
 * Mock parity (COMPARE-ac, 2026-10-29): the kicker is 12/600 in the tertiary ink, the card is the
 * surface fill with no edge and no radius, padded 4 top and bottom so a 44 row sits 48 tall.
 *
 * The label is the block's heading, so it is an `h2` and the card is a
 * `section` named by it — a page of eight unlabelled card divs is unnavigable
 * by heading, which is how most of this family is read.
 */
export default function SettingsGroup({
  label,
  action,
  inset = 'page',
  bodyClassName,
  children,
  className,
}: SettingsGroupProps) {
  return (
    <section aria-label={label} className={cn('flex flex-col gap-2', className)}>
      {label === undefined && action === undefined ? null : (
        <div className="flex min-h-4 items-center justify-between gap-3">
          {label === undefined ? (
            <span />
          ) : (
            <h2 className="text-h-xs text-muted-foreground-subtle">{label}</h2>
          )}
          {action}
        </div>
      )}
      <div className={cn('bg-card py-1 text-card-foreground', INSET[inset], bodyClassName)}>
        {children}
      </div>
    </section>
  )
}
